<template>
  <div class="dashboard-container">
    <!-- Sidebar -->
    <aside class="sidebar" :class="{ 'sidebar-collapsed': isCollapsed }">
      <div class="sidebar-header">
        <button class="toggle-btn" @click="toggleSidebar">
          <span v-if="!isCollapsed">☰</span>
          <span v-else>→</span>
        </button>
        <h1 v-if="!isCollapsed" class="sidebar-title">Property Management</h1>
      </div>
      
      <nav class="sidebar-nav">
        <router-link to="/" class="nav-item">
          <span class="nav-icon">🏠</span>
          <span v-if="!isCollapsed" class="nav-text">Dashboard</span>
        </router-link>
        <router-link to="/reservations" class="nav-item">
          <span class="nav-icon">📋</span>
          <span v-if="!isCollapsed" class="nav-text">Reservations</span>
        </router-link>
        <router-link to="/properties" class="nav-item" active-class="active">
          <span class="nav-icon">🏨</span>
          <span v-if="!isCollapsed" class="nav-text">Properties</span>
        </router-link>
        <a @click="goToEmailContacts" class="nav-item" style="cursor: pointer;">
          <span class="nav-icon">📧</span>
          <span v-if="!isCollapsed" class="nav-text">Email Contacts</span>
        </a>
        <router-link to="/settings" class="nav-item">
          <span class="nav-icon">⚙️</span>
          <span v-if="!isCollapsed" class="nav-text">Settings</span>
        </router-link>
      </nav>
    </aside>

    <!-- Main Content -->
    <main class="main-content">
      <div class="content-header">
        <h1>Properties</h1>
        <div class="user-info">
          <span class="total-properties">{{ totalProperties }} Properties</span>
          <button @click="logout" class="logout-btn">Logout</button>
        </div>
      </div>

      <!-- Properties Grid -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading properties...</p>
      </div>

      <div v-else-if="properties.length > 0" class="properties-grid">
        <div v-for="property in properties" :key="property.id" class="property-card">
          <div class="property-image">
            <div class="property-placeholder">
              <span class="property-icon">🏨</span>
            </div>
            <div class="property-status">
              <span class="status-badge active">Active</span>
            </div>
          </div>
          
          <div class="property-content">
            <div class="property-header">
              <h3 class="property-name">{{ property.name }}</h3>
              <div class="property-id">ID: {{ property.id }}</div>
            </div>
            
            <div class="property-details">
              <div class="detail-item" v-if="property.address">
                <span class="detail-icon">📍</span>
                <span class="detail-text">{{ property.address }}</span>
              </div>
              <div class="detail-item" v-if="property.city">
                <span class="detail-icon">🏙️</span>
                <span class="detail-text">{{ property.city }}, {{ property.postalCode }}</span>
              </div>
              <div class="detail-item" v-if="property.country">
                <span class="detail-icon">🌍</span>
                <span class="detail-text">{{ property.country }}</span>
              </div>
            </div>
            
            <div class="property-stats">
              <div class="stat-item">
                <div class="stat-value">{{ getPropertyRevenue(property.name) }}</div>
                <div class="stat-label">This Month Revenue</div>
              </div>
              <div class="stat-item">
                <div class="stat-value">{{ getPropertyReservations(property.name) }}</div>
                <div class="stat-label">Active Reservations</div>
              </div>
            </div>

            <!-- Property Settings Summary - UPDATED SECTION -->
            <div class="property-settings">
              <div class="settings-header">
                <span class="settings-icon">⚙️</span>
                <span class="settings-title">Configuration</span>
              </div>
              <div class="settings-grid">
                <!-- UPDATED: Management fee now shows the calculation base -->
                <div class="setting-item">
                  <span class="setting-label">Management</span>
                  <span class="setting-value">{{ getManagementFeeBaseDisplay(property.id) }}</span>
                </div>
                <div class="setting-item">
                  <span class="setting-label">VAT</span>
                  <span class="setting-value">{{ getPropertySetting(property.id, 'vatRate') }}%</span>
                </div>
                <div class="setting-item">
                  <span class="setting-label">Maintenance</span>
                  <span class="setting-value">£{{ getPropertySetting(property.id, 'maintenance') }}</span>
                </div>
                <div class="setting-item">
                  <span class="setting-label">Consumables</span>
                  <span class="setting-value">£{{ getPropertySetting(property.id, 'consumables') }}</span>
                </div>
                <div class="setting-item">
                  <span class="setting-label">Cleaning</span>
                  <span class="setting-value">{{ getCleaningModeDisplay(property.id) }}</span>
                </div>
                <div class="setting-item">
                  <span class="setting-label">Card Fees</span>
                  <span class="setting-value">{{ getCardModeDisplay(property.id) }}</span>
                </div>
              </div>
            </div>
            
            <div class="property-footer">
              <div class="currency-info">
                <span class="currency-label">Currency:</span>
                <span class="currency-code">{{ getCurrencyCode(property.primaryCurrencyId) }}</span>
              </div>
              <div class="created-date">
                <span class="created-label">Added:</span>
                <span class="created-value">{{ formatDate(property.createdAt) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="empty-state">
        <div class="empty-icon">🏨</div>
        <h3>No Properties Found</h3>
        <p>No properties are currently configured in your system.</p>
      </div>

      <!-- Summary Section -->
      <div v-if="properties.length > 0" class="summary-section">
        <div class="summary-card">
          <h3>Portfolio Summary</h3>
          <div class="summary-grid">
            <div class="summary-item">
              <div class="summary-value">{{ totalProperties }}</div>
              <div class="summary-label">Total Properties</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">{{ formatCurrency(totalRevenue) }}</div>
              <div class="summary-label">Total Revenue (This Month)</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">{{ totalActiveReservations }}</div>
              <div class="summary-label">Total Active Reservations</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">{{ averageRevenuePerProperty }}</div>
              <div class="summary-label">Avg Revenue/Property</div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios';
import SETTINGS from '@/config/settings.js';

export default {
  name: 'PropertiesView',
  data() {
    return {
      isCollapsed: false,
      loading: false,
      properties: [],
      totalProperties: 0,
      currentMonthReservations: [],
      
      // Currency mapping
      currencies: {
        1: 'GBP',
        38: 'GBP',
        // Add more currency mappings as needed
      },
      
      baseUrl: SETTINGS.API.baseUrl
    };
  },
  
  computed: {
    totalRevenue() {
      return this.currentMonthReservations.reduce((total, res) => total + res.totalReservationPrice, 0);
    },
    
    totalActiveReservations() {
      return this.currentMonthReservations.length;
    },
    
    averageRevenuePerProperty() {
      if (this.totalProperties === 0) return SETTINGS.formatCurrency(0);
      return SETTINGS.formatCurrency(this.totalRevenue / this.totalProperties);
    }
  },
  
  methods: {

    getPropertySetting(propertyId, settingKey) {
      const settings = this.getPropertySettings(propertyId);
      return settings[settingKey] || this.getDefaultSettings()[settingKey];
    },
    
    getPropertySettings(propertyId) {
      const savedSettings = localStorage.getItem('propertySettings');
      if (!savedSettings) return this.getDefaultSettings();
      
      try {
        const allSettings = JSON.parse(savedSettings);
        return allSettings[propertyId] || this.getDefaultSettings();
      } catch (e) {
        return this.getDefaultSettings();
      }
    },
    
    // UPDATED: Default settings with managementFeeBase
    getDefaultSettings() {
      return {
        managementFeeRate: 16,
        managementFeeBase: 'net', // NEW FIELD: 'gross' or 'net'
        vatRate: 20,
        maintenance: 0,
        consumables: 20,
        otherExpenses: 0,
        cleaningMode: 'rentlio',
        bookingCleaningFee: 150,
otherCleaningFee: 120,
        cardFeeMode: 'rentlio',
        customCardFeeRate: 2.9,
        customCardFeeFixed: 0.30
      };
    },
    
    // NEW: Helper method to display management fee base
   // CORRECTED: Helper method to display management fee base
    getManagementFeeBaseDisplay(propertyId) {
      const base = this.getPropertySetting(propertyId, 'managementFeeBase');
      const rate = this.getPropertySetting(propertyId, 'managementFeeRate');
      
      if (base === 'gross') {
        return `${rate}% (Gross Profit)`;
      } else {
        return `${rate}% (Net Income)`;
      }
    },
    
    getCleaningModeDisplay(propertyId) {
  const mode = this.getPropertySetting(propertyId, 'cleaningMode');
  if (mode === 'rentlio') {
    return 'Rentlio';
  } else {
    const bookingFee = this.getPropertySetting(propertyId, 'bookingCleaningFee');
    const otherFee = this.getPropertySetting(propertyId, 'otherCleaningFee');
    return `B:£${bookingFee}, O:£${otherFee}`;
  }
},
    
    getCardModeDisplay(propertyId) {
      const mode = this.getPropertySetting(propertyId, 'cardFeeMode');
      if (mode === 'rentlio') {
        return 'Rentlio';
      } else {
        const rate = this.getPropertySetting(propertyId, 'customCardFeeRate');
        return `${rate}%`;
      }
    },

    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
    },
    
    logout() {
      localStorage.removeItem('token');
      this.$router.push('/login');
    },
    
    formatCurrency(amount) {
      return SETTINGS.formatCurrency(amount || 0);
    },
    
    formatDate(timestamp) {
      return SETTINGS.formatDate(timestamp);
    },
    
    getCurrencyCode(currencyId) {
      return this.currencies[currencyId] || 'GBP';
    },
    
    getPropertyRevenue(propertyName) {
      const propertyReservations = this.currentMonthReservations.filter(
        res => res.unitName === propertyName
      );
      const revenue = propertyReservations.reduce((total, res) => total + res.totalReservationPrice, 0);
      return this.formatCurrency(revenue);
    },
    
    getPropertyReservations(propertyName) {
      return this.currentMonthReservations.filter(
        res => res.unitName === propertyName
      ).length;
    },
    
    async fetchData() {
      const token = localStorage.getItem('token');
      if (!token) {
        this.$router.push('/login');
        return;
      }

      const headers = { 'Authorization': `Bearer ${token}` };
      this.loading = true;

      try {
        // Fetch properties and current month reservations in parallel
        const [propertiesRes, currentMonthRes] = await Promise.all([
          axios.get(`${this.baseUrl}/properties`, { headers }),
          axios.get(`${this.baseUrl}/reports/current`, { headers })
        ]);

        // Set properties data
        this.properties = propertiesRes.data.properties.data;
        this.totalProperties = propertiesRes.data.properties.total;

        // Set current month reservations for revenue calculations
        this.currentMonthReservations = Object.values(currentMonthRes.data.reservations);

      } catch (error) {
        console.error('Error fetching properties:', error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          this.$router.push('/login');
        }
      } finally {
        this.loading = false;
      }
    },

    goToEmailContacts() {
      this.$router.push('/reservations?openContacts=true');
    }
  },
  
  mounted() {
    this.fetchData();
  }
};
</script>

<style scoped>
.dashboard-container {
  display: flex;
  min-height: 100vh;
  background: #f8fafc;
}

/* Sidebar Styles */
.sidebar {
  width: 280px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  transition: width 0.3s ease;
  position: fixed;
  height: 100vh;
  z-index: 1000;
  box-shadow: 2px 0 10px rgba(0,0,0,0.1);
}

.sidebar-collapsed {
  width: 70px;
}

.sidebar-header {
  padding: 20px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
}

.toggle-btn {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  margin-bottom: 15px;
}

.sidebar-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.sidebar-nav {
  padding: 20px 0;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  color: rgba(255,255,255,0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  margin: 0 10px;
  border-radius: 10px;
}

.nav-item:hover,
.nav-item.active {
  background: rgba(255,255,255,0.1);
  color: white;
  transform: translateX(5px);
}

.nav-icon {
  font-size: 18px;
  margin-right: 15px;
  width: 20px;
}

.nav-text {
  font-weight: 500;
}

/* Main Content */
.main-content {
  flex: 1;
  margin-left: 280px;
  padding: 30px;
  transition: margin-left 0.3s ease;
}

.sidebar-collapsed + .main-content {
  margin-left: 70px;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.content-header h1 {
  font-size: 32px;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 20px;
}

.total-properties {
  background: #667eea;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 14px;
}

.logout-btn {
  background: #e53e3e;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.3s ease;
}

.logout-btn:hover {
  background: #c53030;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
  color: #64748b;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Properties Grid */
.properties-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 25px;
  margin-bottom: 40px;
}

.property-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 25px rgba(0,0,0,0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.property-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 35px rgba(0,0,0,0.15);
}

.property-image {
  position: relative;
  height: 140px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.property-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
}

.property-icon {
  font-size: 48px;
  opacity: 0.7;
}

.property-status {
  position: absolute;
  top: 15px;
  right: 15px;
}

.status-badge {
  background: rgba(255,255,255,0.9);
  color: #22543d;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.active {
  background: #c6f6d5;
  color: #22543d;
}

.property-content {
  padding: 25px;
}

.property-header {
  margin-bottom: 20px;
}

.property-name {
  font-size: 20px;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 5px 0;
  line-height: 1.3;
}

.property-id {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.property-details {
  margin-bottom: 20px;
}

.detail-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
  color: #4a5568;
}

.detail-icon {
  margin-right: 8px;
  width: 16px;
  opacity: 0.7;
}

.detail-text {
  line-height: 1.4;
}

.property-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
}

/* Property Settings Summary */
.property-settings {
  margin-top: 15px;
  padding: 15px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.settings-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e2e8f0;
}

.settings-icon {
  font-size: 12px;
  opacity: 0.7;
}

.settings-title {
  font-size: 11px;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.settings-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  background: white;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.setting-item:hover {
  border-color: #667eea;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.setting-label {
  font-size: 10px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.setting-value {
  font-size: 11px;
  color: #1a202c;
  font-weight: 600;
  background: #e2e8f0;
  color: #374151;
  padding: 3px 8px;
  border-radius: 4px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  border: 1px solid #d1d5db;
  min-width: 45px;
  text-align: center;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .property-settings {
    margin-top: 12px;
    padding: 12px;
  }
  
  .settings-grid {
    grid-template-columns: 1fr;
    gap: 6px;
  }
  
  .setting-item {
    padding: 5px 6px;
  }
  
  .setting-label {
    font-size: 9px;
  }
  
  .setting-value {
    font-size: 8px;
    padding: 1px 4px;
  }
}

.stat-item {
  text-align: center;
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.property-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid #e2e8f0;
  font-size: 12px;
}

.currency-info,
.created-date {
  display: flex;
  align-items: center;
  gap: 5px;
}

.currency-label,
.created-label {
  color: #64748b;
  font-weight: 500;
}

.currency-code {
  background: #667eea;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 10px;
}

.created-value {
  color: #4a5568;
  font-weight: 500;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
  text-align: center;
  color: #64748b;
}

.empty-icon {
  font-size: 80px;
  margin-bottom: 20px;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 10px 0;
  color: #1a202c;
}

.empty-state p {
  font-size: 16px;
  margin: 0;
  max-width: 400px;
}

/* Summary Section */
.summary-section {
  margin-top: 40px;
}

.summary-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 25px rgba(0,0,0,0.08);
  padding: 30px;
  border: 1px solid #e2e8f0;
}

.summary-card h3 {
  font-size: 24px;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 25px 0;
  text-align: center;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 30px;
}

.summary-item {
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.summary-value {
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 8px;
}

.summary-label {
  font-size: 14px;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }
  
  .main-content {
    margin-left: 0;
    padding: 20px 15px;
  }
  
  .properties-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .content-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .user-info {
    width: 100%;
    justify-content: space-between;
  }
  
  .property-stats {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  
  
  .property-footer {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
  
  .summary-grid {
    grid-template-columns: 1fr 1fr;
    gap: 15px;
  }
  
  .summary-item {
    padding: 15px;
  }
  
  .summary-value {
    font-size: 20px;
  }
}

@media (max-width: 480px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>